import { useMemo } from 'react';
import { AlertTriangle, User, Shield, Calendar, FileText } from 'lucide-react';
import { LABEL_CATEGORY } from '@vagusx/shared';
import {
  FilterBar,
  type FilterDefinition,
  type FilterState,
} from '@/components/filter-bar/filter-bar';
import { useLabelOptions } from '@/lib/select-label-options';

const STATIC_DEFINITIONS: FilterDefinition[] = [
  {
    key: 'urgency',
    label: 'Priority',
    type: 'multi-select',
    icon: AlertTriangle,
    options: [
      { value: 'stat', label: 'Stat' },
      { value: 'urgent', label: 'Urgent' },
      { value: 'routine', label: 'Routine' },
    ],
  },
  {
    key: 'referringProviderName',
    label: 'Referring Provider',
    type: 'text',
    icon: User,
    operators: [{ value: 'contains', label: 'contains' }],
  },
  {
    key: 'healthPlan',
    label: 'Health Plan',
    type: 'text',
    icon: Shield,
    operators: [{ value: 'contains', label: 'contains' }],
  },
  {
    key: 'date',
    label: 'Date',
    type: 'date-range',
    icon: Calendar,
  },
];

function useDocumentFilterDefinitions(): FilterDefinition[] {
  const options = useLabelOptions({ category: LABEL_CATEGORY.DOCUMENT_TYPE });

  return useMemo(() => {
    // Hide the Document Type filter entirely when the tenant has no
    // document_type labels configured — an empty picker would just
    // confuse users (they'd think the filter is broken). Once they
    // add labels in /setup/labels, the chip reappears automatically.
    if (options.length === 0) return STATIC_DEFINITIONS;

    const classificationDef: FilterDefinition = {
      key: 'classification',
      label: 'Document Type',
      type: 'multi-select',
      icon: FileText,
      options,
    };

    return [...STATIC_DEFINITIONS, classificationDef];
  }, [options]);
}

interface DocumentFilterBarProps {
  filterState: FilterState;
  onFilterChange: (state: FilterState) => void;
}

export function DocumentFilterBar({
  filterState,
  onFilterChange,
}: DocumentFilterBarProps): React.ReactElement {
  const definitions = useDocumentFilterDefinitions();
  return <FilterBar definitions={definitions} value={filterState} onChange={onFilterChange} />;
}
